export const getWeatherIconClass = (condition, isDay = true) => {
  // If condition is not provided or empty, return default icon
  if (!condition) {
    return isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy';
  }
  
  // Convert condition to lowercase and remove any extra spaces
  const normalizedCondition = condition.toLowerCase().trim();
  
  // Handle specific abbreviations first (common in hourly forecast data)
  if (normalizedCondition === 'm cloudy') {
    return isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy';
  }
  
  if (normalizedCondition === 'p cloudy') {
    return isDay ? 'wi-day-sunny-overcast' : 'wi-night-alt-partly-cloudy';
  }
  
  // Special handling for other single-letter prefix conditions
  const singleLetterMatch = normalizedCondition.match(/^([a-z]) (cloudy|rain|snow|clear|sunny|fog)$/i);
  if (singleLetterMatch) {
    const prefix = singleLetterMatch[1].toLowerCase();
    const baseCondition = singleLetterMatch[2].toLowerCase();
    
    if (prefix === 'm') { // Mostly
      if (baseCondition === 'cloudy') {
        return isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy';
      } else if (baseCondition === 'sunny' || baseCondition === 'clear') {
        return isDay ? 'wi-day-sunny-overcast' : 'wi-night-alt-partly-cloudy';
      }
    } else if (prefix === 'p') { // Partly
      if (baseCondition === 'cloudy') {
        return isDay ? 'wi-day-sunny-overcast' : 'wi-night-alt-partly-cloudy';
      } else if (baseCondition === 'sunny' || baseCondition === 'clear') {
        return isDay ? 'wi-day-cloudy-high' : 'wi-night-alt-cloudy-high';
      }
    }
  }
  
  // More detailed condition map with better differentiation between cloud types
  const conditionMap = {
    // Clear conditions
    'clear': isDay ? 'wi-day-sunny' : 'wi-night-clear',
    'sunny': 'wi-day-sunny',
    'fair': isDay ? 'wi-day-sunny' : 'wi-night-clear',
    
    // Partly cloudy conditions
    'partly cloudy': isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy',
    'mostly sunny': isDay ? 'wi-day-sunny-overcast' : 'wi-night-alt-partly-cloudy',
    'partly sunny': isDay ? 'wi-day-cloudy-high' : 'wi-night-alt-cloudy-high',
    'mostly clear': isDay ? 'wi-day-sunny-overcast' : 'wi-night-alt-partly-cloudy',
    'scattered clouds': isDay ? 'wi-day-cloudy-high' : 'wi-night-alt-cloudy-high',
    'sct clouds': isDay ? 'wi-day-cloudy-high' : 'wi-night-alt-cloudy-high',
    'few clouds': isDay ? 'wi-day-sunny-overcast' : 'wi-night-alt-partly-cloudy',
    
    // Cloudy conditions
    'cloudy': 'wi-cloudy',
    'mostly cloudy': isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy',
    'broken clouds': isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy',
    'overcast': 'wi-cloudy',
    
    // Rain conditions
    'rain': 'wi-rain',
    'light rain': 'wi-sprinkle',
    'heavy rain': 'wi-rain-wind',
    'moderate rain': 'wi-rain-mix',
    'rain showers': isDay ? 'wi-day-showers' : 'wi-night-alt-showers',
    'chance of rain': isDay ? 'wi-day-rain' : 'wi-night-alt-rain',
    'chance of showers': isDay ? 'wi-day-showers' : 'wi-night-alt-showers',
    'scattered showers': isDay ? 'wi-day-showers' : 'wi-night-alt-showers',
    'sct showers': isDay ? 'wi-day-showers' : 'wi-night-alt-showers',
    'isolated showers': isDay ? 'wi-day-sprinkle' : 'wi-night-alt-sprinkle',
    'iso showers': isDay ? 'wi-day-sprinkle' : 'wi-night-alt-sprinkle',
    
    // Thunderstorm conditions
    'thunderstorm': 'wi-thunderstorm',
    'thunderstorms': 'wi-thunderstorm',
    'tstorm': 'wi-thunderstorm',
    'tstorms': 'wi-thunderstorm',
    't-storm': 'wi-thunderstorm',
    't-storms': 'wi-thunderstorm',
    'chance of tstorm': isDay ? 'wi-day-thunderstorm' : 'wi-night-alt-thunderstorm',
    'chance of thunderstorm': isDay ? 'wi-day-thunderstorm' : 'wi-night-alt-thunderstorm',
    'scattered thunderstorms': isDay ? 'wi-day-thunderstorm' : 'wi-night-alt-thunderstorm',
    'sct thunderstorms': isDay ? 'wi-day-thunderstorm' : 'wi-night-alt-thunderstorm',
    'sct t-storms': isDay ? 'wi-day-thunderstorm' : 'wi-night-alt-thunderstorm',
    'isolated thunderstorms': isDay ? 'wi-day-lightning' : 'wi-night-alt-lightning',
    'iso thunderstorms': isDay ? 'wi-day-lightning' : 'wi-night-alt-lightning',
    'iso t-storms': isDay ? 'wi-day-lightning' : 'wi-night-alt-lightning',
    
    // Snow conditions
    'snow': 'wi-snow',
    'light snow': 'wi-snow',
    'heavy snow': 'wi-snow-wind',
    'snow showers': isDay ? 'wi-day-snow' : 'wi-night-alt-snow',
    'chance of snow': isDay ? 'wi-day-snow' : 'wi-night-alt-snow',
    'flurries': isDay ? 'wi-day-snow-wind' : 'wi-night-alt-snow-wind',
    
    // Mixed precipitation
    'sleet': 'wi-sleet',
    'freezing rain': 'wi-rain-mix',
    'wintry mix': 'wi-rain-mix',
    'mixed precipitation': 'wi-rain-mix',
    
    // Fog/low visibility conditions
    'fog': 'wi-fog',
    'mist': 'wi-fog',
    'haze': 'wi-day-haze',
    'smoke': 'wi-smoke',
    
    // Wind conditions
    'windy': 'wi-windy',
    'breezy': 'wi-strong-wind',
    'blustery': 'wi-strong-wind',
    
    // Severe weather
    'tornado': 'wi-tornado',
    'hurricane': 'wi-hurricane',
    'tropical storm': 'wi-storm-showers',
    'dust': 'wi-dust',
    'sand': 'wi-sandstorm',
    'volcanic ash': 'wi-volcano',
    'squalls': 'wi-strong-wind',
    
    // Other conditions
    'hail': 'wi-hail',
    'drizzle': 'wi-sprinkle',
    'showers': isDay ? 'wi-day-showers' : 'wi-night-alt-showers',
    'hot': isDay ? 'wi-hot' : 'wi-night-clear',
    'cold': 'wi-snowflake-cold',
    
    // Common forecast phrases (will match partially)
    'sunshine': isDay ? 'wi-day-sunny' : 'wi-night-clear',
    'mix of sun and clouds': isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy',
    'considerable cloudiness': 'wi-cloudy',
    'periods of clouds': isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy',
    'variable clouds': isDay ? 'wi-day-cloudy' : 'wi-night-alt-cloudy',
  };

  // First try direct match
  if (conditionMap[normalizedCondition]) {
    return conditionMap[normalizedCondition];
  }
  
  // If no direct match, search for partial matches
  // Sort the condition keys by length (descending) to match more specific terms first
  const conditionKeys = Object.keys(conditionMap).sort((a, b) => b.length - a.length);
  
  for (const key of conditionKeys) {
    if (normalizedCondition.includes(key)) {
      return conditionMap[key];
    }
  }

  // Log the unrecognized condition
  console.log(`No weather icon match found for: "${normalizedCondition}"`);
  return 'wi-cloudy';
};

export const getWindIconClass = (windSpeed, windDirection) => {
  // Handle null or undefined windDirection
  if (!windDirection) {
    return `wi wi-wind wi-wind-beaufort-${Math.min(Math.floor(windSpeed / 8), 12)}`;
  }
  
  // Convert wind direction to degrees if it's in cardinal form
  const directionMap = {
    'N': 0,
    'NNE': 22.5,
    'NE': 45,
    'ENE': 67.5,
    'E': 90,
    'ESE': 112.5,
    'SE': 135,
    'SSE': 157.5,
    'S': 180,
    'SSW': 202.5,
    'SW': 225,
    'WSW': 247.5,
    'W': 270,
    'WNW': 292.5,
    'NW': 315,
    'NNW': 337.5
  };

  let degrees;
  
  if (typeof windDirection === 'string') {
    // Check if the wind direction is a known abbreviation
    if (directionMap[windDirection.toUpperCase()]) {
      degrees = directionMap[windDirection.toUpperCase()];
    } 
    // Handle two-character directions like SW, NE, etc.
    else if (windDirection.length === 2) {
      const direction = windDirection.toUpperCase();
      if (direction === 'SW') degrees = 225;
      else if (direction === 'SE') degrees = 135;
      else if (direction === 'NW') degrees = 315;
      else if (direction === 'NE') degrees = 45;
      else if (direction === 'W') degrees = 270;
      else if (direction === 'E') degrees = 90;
      else if (direction === 'N') degrees = 0;
      else if (direction === 'S') degrees = 180;
    }
    // Handle three-character directions like SSW, WSW, etc.
    else if (windDirection.length === 3) {
      const direction = windDirection.toUpperCase();
      if (direction === 'SSW') degrees = 202.5;
      else if (direction === 'WSW') degrees = 247.5;
      else if (direction === 'SSE') degrees = 157.5;
      else if (direction === 'ESE') degrees = 112.5;
      else if (direction === 'NNW') degrees = 337.5;
      else if (direction === 'WNW') degrees = 292.5;
      else if (direction === 'NNE') degrees = 22.5;
      else if (direction === 'ENE') degrees = 67.5;
    }
  } else {
    degrees = windDirection;
  }
  
  // If degrees is undefined (direction not found in map), default to 0
  if (degrees === undefined) {
    console.log(`Wind direction not found: "${windDirection}"`);
    degrees = 0;
  }
  
  // Round to the nearest degree for exact matching with the wind CSS classes
  const roundedDegrees = Math.round(degrees);
  
  // Get Beaufort scale indicator based on wind speed
  let beaufortClass = '';
  if (windSpeed >= 0 && windSpeed < 1) {
    beaufortClass = 'wi-wind-beaufort-0';
  } else if (windSpeed >= 1 && windSpeed < 4) {
    beaufortClass = 'wi-wind-beaufort-1';
  } else if (windSpeed >= 4 && windSpeed < 8) {
    beaufortClass = 'wi-wind-beaufort-2';
  } else if (windSpeed >= 8 && windSpeed < 13) {
    beaufortClass = 'wi-wind-beaufort-3';
  } else if (windSpeed >= 13 && windSpeed < 19) {
    beaufortClass = 'wi-wind-beaufort-4';
  } else if (windSpeed >= 19 && windSpeed < 25) {
    beaufortClass = 'wi-wind-beaufort-5';
  } else if (windSpeed >= 25 && windSpeed < 32) {
    beaufortClass = 'wi-wind-beaufort-6';
  } else if (windSpeed >= 32 && windSpeed < 39) {
    beaufortClass = 'wi-wind-beaufort-7';
  } else if (windSpeed >= 39 && windSpeed < 47) {
    beaufortClass = 'wi-wind-beaufort-8';
  } else if (windSpeed >= 47 && windSpeed < 55) {
    beaufortClass = 'wi-wind-beaufort-9';
  } else if (windSpeed >= 55 && windSpeed < 64) {
    beaufortClass = 'wi-wind-beaufort-10';
  } else if (windSpeed >= 64 && windSpeed < 73) {
    beaufortClass = 'wi-wind-beaufort-11';
  } else if (windSpeed >= 73) {
    beaufortClass = 'wi-wind-beaufort-12';
  }
  
  // Adjust the degrees for the from-XXX-deg class
  // We need to rotate by 180 degrees since "from-" in the weather icons
  // expects the direction the wind comes from
  const adjustedDegrees = (roundedDegrees + 180) % 360;
  
  // Return the combined classes
  return `wi wi-wind from-${adjustedDegrees}-deg ${beaufortClass}`;
}; 